import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  decisionPayload,
  normalizeApproval,
  selectApprovals,
} from "./ceoApprovalsModel";
import "./ceoApprovals.css";
const date = (value) =>
  value && !Number.isNaN(Date.parse(value))
    ? new Date(value).toLocaleString("en-NG", { timeZone: "Africa/Lagos" })
    : "—";
export default function CEOApprovals({ loadApprovals, onDecision }) {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "PENDING";
  const [data, setData] = useState([]),
    [query, setQuery] = useState(""),
    [type, setType] = useState(""),
    [selected, setSelected] = useState(null),
    [choice, setChoice] = useState(""),
    [reason, setReason] = useState(""),
    [loading, setLoading] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [refresh, setRefresh] = useState(0);
  const lock = useRef(false),
    key = useRef(null);
  const setStatus = (value) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("status", value);
      return next;
    });
  };
  useEffect(() => {
    if (!loadApprovals) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => loadApprovals({ signal: controller.signal }))
      .then((result) => {
        if (controller.signal.aborted) return;
        const rows = Array.isArray(result) ? result : result?.requests;
        if (!Array.isArray(rows))
          throw new Error("Approvals response must contain a requests array.");
        rows.forEach(normalizeApproval);
        setData(rows);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e.message || "Unable to load approvals.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [loadApprovals, refresh]);
  const current = data.find((item) => item.id === selected?.id) || selected;
  const types = [...new Set(data.map((item) => item.type))].sort();
  const visible = selectApprovals(data, { status, type, query });
  const open = (item) => {
    setSelected(item);
    setChoice("");
    setReason("");
    key.current = null;
    setError("");
  };
  const decide = async () => {
    if (lock.current || !onDecision || !current) return;
    setError("");
    setNotice("");
    let payload;
    try {
      payload = decisionPayload(current, choice, reason);
    } catch (e) {
      setError(e.message);
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      key.current ||=  window.crypto.randomUUID();
      const result = await onDecision(payload, { idempotencyKey: key.current });
      if (![200, 201].includes(Number(result?.status)))
        throw new Error(
          result?.message || "The server did not confirm the decision.",
        );
      setNotice(result.message || "Decision recorded.");
      setSelected(null);
      setChoice("");
      setReason("");
      key.current = null;
      setRefresh((n) => n + 1);
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.message ||
          "Decision failed. Retry uses the same request key.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <section className="ceo-approvals">
      <div className="ceo-approvals__head">
        <div>
          <h2>Executive decisions</h2>
          <p className="ceo-subtle">
            Review requests and record accountable decisions.
          </p>
        </div>
        <button
          className="ceo-button"
          type="button"
          disabled={!loadApprovals || loading || busy}
          onClick={() => setRefresh((n) => n + 1)}
        >
          Refresh
        </button>
      </div>
      {error && (
        <p role="alert" className="ceo-approvals__error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="ceo-approvals__notice">
          {notice}
        </p>
      )}
      {loading && <p role="status">Loading approvals…</p>}
      {!loadApprovals && (
        <p className="ceo-surface ceo-approvals__empty">
          Connect the CEO approvals service to view requests.
        </p>
      )}
      <div className="ceo-surface ceo-approvals__filters">
        <div className="ceo-approvals__tabs">
          {["PENDING", "APPROVED", "REJECTED", "ALL"].map((value) => (
            <button
              type="button"
              key={value}
              aria-pressed={status === value}
              onClick={() => setStatus(value)}
            >
              {value.toLowerCase()}
            </button>
          ))}
        </div>
        <label>
          Search{" "}
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Request, staff or business"
          />
        </label>
        <label>
          Type{" "}
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {types.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="ceo-approvals__layout">
        <div
          className="ceo-surface ceo-approvals__list"
          aria-label="Approval requests"
        >
          {visible.map((item) => (
            <button
              type="button"
              className="ceo-approvals__row"
              aria-current={current?.id === item.id ? "true" : undefined}
              key={item.id}
              onClick={() => open(item)}
            >
              <span>
                <strong>{item.title || item.type}</strong>
                <small>
                  {item.requesterName || "Unknown requester"} ·{" "}
                  {item.subjectName || item.id}
                </small>
                <small>{date(item.createdAt)}</small>
              </span>
              <span className="ceo-approvals__badge">{item.status}</span>
            </button>
          ))}
          {!loading && !visible.length && (
            <p className="ceo-subtle">No matching requests.</p>
          )}
        </div>
        <div className="ceo-surface ceo-approvals__detail">
          {current ? (
            <>
              <div className="ceo-approvals__head">
                <div>
                  <span className="ceo-subtle">
                    {current.type} · {current.id}
                  </span>
                  <h3>{current.title || current.type}</h3>
                </div>
                <span className="ceo-approvals__badge">{current.status}</span>
              </div>
              <dl>
                <dt>Requester</dt>
                <dd>
                  {current.requesterName || "—"}{" "}
                  {current.requesterRole && `(${current.requesterRole})`}
                </dd>
                <dt>Subject</dt>
                <dd>{current.subjectName || "—"}</dd>
                <dt>Submitted</dt>
                <dd>{date(current.createdAt)}</dd>
                <dt>Reason</dt>
                <dd>{current.reason || "—"}</dd>
                {current.amount != null && (
                  <>
                    <dt>Amount</dt>
                    <dd>
                      {new Intl.NumberFormat("en-NG", {
                        style: "currency",
                        currency: current.currency || "NGN",
                      }).format(Number(current.amount))}
                    </dd>
                  </>
                )}
              </dl>
              <p className="ceo-approvals__description">
                {current.description || "No further description supplied."}
              </p>
              {current.status === "PENDING" && (
                <>
                  <div className="ceo-approvals__actions">
                    <button
                      type="button"
                      aria-pressed={choice === "APPROVE"}
                      onClick={() => {
                        setChoice("APPROVE");
                        key.current = null;
                      }}
                      disabled={busy}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      aria-pressed={choice === "REJECT"}
                      onClick={() => {
                        setChoice("REJECT");
                        key.current = null;
                      }}
                      disabled={busy}
                    >
                      Reject
                    </button>
                  </div>
                  {choice && (
                    <div className="ceo-approvals__confirm">
                      <p>
                        Confirm {choice.toLowerCase()} for this request. The
                        server will verify its current state before applying the
                        decision.
                      </p>
                      <label>
                        Decision reason{" "}
                        {choice === "REJECT" ? "*" : "(optional)"}
                        <textarea
                          value={reason}
                          disabled={busy}
                          onChange={(e) => {
                            setReason(e.target.value);
                            key.current = null;
                          }}
                        />
                      </label>
                      <button
                        className="ceo-button ceo-button--primary"
                        type="button"
                        disabled={busy || !onDecision}
                        onClick={decide}
                      >
                        {busy ? "Saving…" : `Confirm ${choice.toLowerCase()}`}
                      </button>
                      {!onDecision && (
                        <p className="ceo-subtle">
                          Connect an authorized decision endpoint.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
              {current.status !== "PENDING" && (
                <p>
                  Decision recorded by{" "}
                  {current.decidedByName ||
                    current.decidedBy ||
                    "the approval service"}{" "}
                  on {date(current.decidedAt)}.
                </p>
              )}
            </>
          ) : (
            <p className="ceo-subtle">
              Select a request to inspect its details.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
